import { observer } from 'mobx-react-lite'
import { type ChangeEvent, useCallback, useEffect, useState, type FocusEvent } from 'react'

import { PageHeader } from '@/shared'
import { authState, servicesState } from '@App/store'
import { type ISwapSequencs } from '@App/types/common'
import {
    type ITransferServiceDto,
    type ICategoryDto,
    type ISectionRulesWrapper,
    type IServiceRulesWrapper,
    type IChildServiceDec
} from '@App/types/servicesTypes'
import { onGetServicesByTitle } from '@App/utils/onGetServicesByTitle'

import { Categories } from './Categories/Categories'
import { SearchService } from './SearchService/SearchService'
import style from './Services.module.scss'
import { ServiceTransferModal } from './ServiceTransferModal/ServiceTransferModal'

export const Services = observer(() => {
    const [searchState, setSearchState] = useState<IChildServiceDec[]>([])

    const getAllCategories = useCallback(() => {
        if (!servicesState.categoriesList) {
            servicesState.onGetAllCategories()
        }
    }, [])

    const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        const minTextLength = value.length >= 3
        if (servicesState.categoriesList && minTextLength) {
            setSearchState(onGetServicesByTitle(servicesState.categoriesList, value.trim()))
        } else {
            setSearchState([])
        }
    }

    const onSearchByFocus = (e: FocusEvent<HTMLInputElement>) => {
        const { value } = e.target
        if (servicesState.categoriesList && !!value) {
            setSearchState(onGetServicesByTitle(servicesState.categoriesList, value))
        }
    }

    const addCategory = useCallback((dto: ICategoryDto, onSuccess: () => void) => {
        servicesState.onAddCategory({ dto, onSuccess })
    }, [])

    const editCategory = useCallback((dto: ICategoryDto, onSuccess: () => void) => {
        servicesState.onEditCategory({ dto, onSuccess })
    }, [])

    const removeCategory = useCallback((id: string, onSuccess: () => void) => {
        servicesState.onRemoveCategory({ id, onSuccess })
    }, [])

    const addSection = useCallback((dto: ICategoryDto, onSuccess: () => void) => {
        servicesState.onAddCategory({ dto, onSuccess })
    }, [])

    const transferService = useCallback((dto: ITransferServiceDto) => {
        const onSuccess = () => {
            servicesState.onSetTranferSevice(null)
        }
        servicesState.onTransferService({ dto, onSuccess })
    }, [])

    const getServiceRules = useCallback((code: string) => {
        servicesState.onGetServiceRules(code)
    }, [])

    const changeServiceRules = useCallback(
        (dto: IServiceRulesWrapper, onSuccess?: () => void, withFetching?: boolean) => {
            const onSuccessChangeRule = () => {
                onSuccess?.()
            }
            servicesState.onChangePreparingServiceRulesData({ dto, onSuccess: onSuccessChangeRule, withFetching })
        },
        []
    )

    const getSectionsRules = useCallback((id: string) => {
        servicesState.onGetSectionsRules(id)
    }, [])

    const changeRules = useCallback((dto: ISectionRulesWrapper, onSuccess?: () => void, withFetching?: boolean) => {
        servicesState.onChangePreparingRulesData({ dto, onSuccess, withFetching })
    }, [])

    const swapCategories = useCallback((dto: ISwapSequencs) => {
        servicesState.onSwapCategories({ dto })
    }, [])

    const swapServices = useCallback((dto: ISwapSequencs) => {
        servicesState.onSwapServices({ dto })
    }, [])

    useEffect(getAllCategories, [getAllCategories])

    return (
        <div className={style.container}>
            {servicesState.categoriesList && servicesState.tranferSevice && (
                <ServiceTransferModal
                    isFetching={servicesState.isFetching}
                    serviceRules={servicesState.serviceRules}
                    tranferredService={servicesState.tranferSevice}
                    sections={servicesState.categoriesList}
                    onGetServiceRules={getServiceRules}
                    onChangeRules={changeServiceRules}
                    onTransferService={transferService}
                    setTranferredService={servicesState.onSetTranferSevice}
                    onSetServiceRules={servicesState.onSetServiceRules}
                />
            )}
            <PageHeader pageName={'Услуги'} pageInfo={null} />
            <div className={style.searchWrapper}>
                <SearchService
                    onSetTranferSevice={servicesState.onSetTranferSevice}
                    onSearch={onSearch}
                    onSearchByFocus={onSearchByFocus}
                    placeholder='Поиск услуги'
                    searchState={searchState}
                    setSearchState={setSearchState}
                />
            </div>
            <Categories
                isFetching={servicesState.isFetching}
                categoriesList={servicesState.categoriesList}
                adminRoles={authState.userData?.roles}
                sectionRules={servicesState.sectionRules}
                onChangeRules={changeRules}
                onSetTranferSevice={servicesState.onSetTranferSevice}
                onSetSectionRules={servicesState.onSetSectionRules}
                onSwapCategories={swapCategories}
                onSwapServices={swapServices}
                onGetSectionsRules={getSectionsRules}
                onAddCategory={addCategory}
                onRemoveCategory={removeCategory}
                onEditCategory={editCategory}
                onAddSection={addSection}
                onRemoveSection={removeCategory}
            />
        </div>
    )
})
